import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import {
  IncorrectTimeNote,
  Issue,
  Product,
  Project,
  TimeLog,
  UserInfo,
} from '../utils/model';

export interface ReportRecord {
  author: UserInfo;
  total: number;
  records: TimeLog[];
}

export interface ReportData {
  loading?: string;
  error?: string;
  incorrectTimeNotes: IncorrectTimeNote[];
  dangerIssues: Issue[];
  data: ReportRecord[];
}

export interface ProductState {
  product?: Product;
  projects: Project[];
  report: ReportData;
  combineIssues: boolean;
  validateTimeNotes: boolean;
}

const initialState: ProductState = {
  product: undefined,
  projects: [],
  validateTimeNotes: localStorage.getItem('ValidateTimeNotes') === 'true',
  combineIssues: localStorage.getItem('CombineIssues') !== 'false',
  report: { data: [], dangerIssues: [], incorrectTimeNotes: [] },
};

export const productSlice = createSlice({
  name: 'product',
  initialState,
  reducers: {
    setProduct: (state, action: PayloadAction<Product>) => {
      state.product = action.payload;
    },
    setProjects: (state, action: PayloadAction<Project[]>) => {
      if (state.projects.length === action.payload.length) return;
      state.projects = action.payload;
    },
    setReportLoading: (state, action: PayloadAction<string | undefined>) => {
      state.report.loading = action.payload;
    },
    setReportError: (state, action: PayloadAction<string | undefined>) => {
      state.report.error = action.payload;
    },
    setReportData: (state, action: PayloadAction<ReportRecord[]>) => {
      state.report.data = [...action.payload].map((r) => ({
        author: r.author,
        records: [...r.records],
        total: r.total,
      }));
    },
    setCombineIssues: (state, action: PayloadAction<boolean>) => {
      localStorage.setItem('CombineIssues', action.payload ? 'true' : 'false');
      state.combineIssues = action.payload;
    },
    setValidateTimeNotes: (state, action: PayloadAction<boolean>) => {
      localStorage.setItem('ValidateTimeNotes', action.payload ? 'true' : 'false');
      state.validateTimeNotes = action.payload;
    },
    setIncorrectTimeNotes: (state, action: PayloadAction<IncorrectTimeNote[]>) => {
      state.report.incorrectTimeNotes = action.payload;
    },
    setDangerIssues: (state, action: PayloadAction<Issue[]>) => {
      state.report.dangerIssues = action.payload;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  setProjects,
  setProduct,
  setReportData,
  setReportError,
  setReportLoading,
  setCombineIssues,
  setDangerIssues,
  setValidateTimeNotes,
  setIncorrectTimeNotes,
} = productSlice.actions;

export const productReducer = productSlice.reducer;
